import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import { sendEscposBase64 } from '../lib/eposPrint'
import { onPaymentDone } from '../lib/paymentDone'

/**
 * 案A: どの店舗ページでも注文伝票を自動印刷する常駐フック。
 *
 * 以前は厨房ページ限定だった自動印刷ループを StoreLayout に載せ替え、オーナーが
 * 実際に見ている注文管理ページなど**どの店舗ページを開いていても**印刷されるように
 * する。橋渡し(USB) / ネットワークいずれの ePOS 設定でも同じ。
 *
 * 二重印刷防止:
 *   - localStorage の printed / failures セットで「印刷済み・失敗」を永続管理
 *   - module-level `inFlightPrints` で同一注文の同時送信をガード（複数マウント時も安全）
 * これにより StoreKitchen の手動リトライ等と併存しても同じ伝票が二度出ない。
 */

const printedKey = (storeId: string) => `yokoso.printed_orders.${storeId}`
const failuresKey = (storeId: string) => `yokoso.print_failures.${storeId}`
// #1167 会計レシート用（厨房伝票とは独立管理。id は `tx:` / `order:` prefix 付き）
const receiptPrintedKey = (storeId: string) => `yokoso.printed_receipts.${storeId}`
const receiptFailuresKey = (storeId: string) => `yokoso.receipt_failures.${storeId}`

/** プリンター設定の保存時に dispatch すると、常駐フックが即座に設定を再読込する。 */
export const PRINTER_CONFIG_CHANGED = 'yokoso:printer-config-changed'

// 全マウント共有の in-flight ガード。StoreKitchen の手動リトライも同じ Set を使い、
// 常駐ループと同一注文を同時送信しない（二重印刷防止）。
export const inFlightPrints = new Set<string>()

// #1178 FB: 店舗ページを複数タブで開いていると各タブの常駐フックが同時に検知し、
// localStorage の printed 記録が間に合わず同じ伝票が2枚出る。Web Locks API で
// タブ間の排他を取り、ロックを取れなかったタブはスキップする (印刷済み記録は
// localStorage 共有なので次 tick で printed 判定になる)。非対応ブラウザは従来動作。
async function withCrossTabLock(key: string, fn: () => Promise<void>): Promise<void> {
  const locks = (navigator as { locks?: LockManager }).locks
  if (locks?.request) {
    await locks.request(`yokoso-print:${key}`, { ifAvailable: true }, async (lock) => {
      if (!lock) return // 別タブが同じ対象を印刷中
      await fn()
    })
  } else {
    await fn()
  }
}

// #1222 厨房伝票の時間窓。作成から一定時間を超えた注文は自動印刷しない。
// これまで厨房経路には時間窓が無く、logout 等で localStorage の印刷済み記録が
// 消えると「過去日の received 注文」まで一斉に再印刷されていた。サーバ側の
// kitchenPrintedAt (本命の防御) と併せ、印刷済み記録が失われた時の被害を限定する。
//
// #1960 **12 時間から 2 時間に縮めた。** 橋渡しを半日止めて繋ぎ直すと、その間の
// 注文が一気に出ていた。厨房伝票は「今すぐ作るもの」なので、2 時間を超えた
// 未印刷の注文を自動で刷る理由が無い。
const KITCHEN_WINDOW_MS = 2 * 60 * 60 * 1000

//: #1960 これ以上溜まっていたら、いきなり刷らずに一度聞く。
//:
//: 1〜2 件は通常営業の遅れ (少し前の注文がまだ刷れていない) なので黙って刷る。
//: 毎回聞くと厨房の邪魔になる。3 件以上は「しばらく繋がっていなかった」で、
//: そのまま流すと紙が何枚も出て止められない。
const KITCHEN_BURST_THRESHOLD = 3

//: 確認を ✕ で閉じたとき、次に聞くまでの時間。閉じ込めないための逃げ道。
const KITCHEN_BURST_SNOOZE_MS = 5 * 60 * 1000

type KitchenOrder = {
  id: string
  status: string
  createdAt?: string | number
  // #1222 サーバ側の印刷済み記録。端末跨ぎ・logout でも二重印刷しないための真実。
  kitchenPrintedAt?: string | number | null
}
type PrinterCfg = { eposEnabled: boolean; eposPrinterUrl: string }

function readSet(key: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]') as string[])
  } catch {
    return new Set()
  }
}

function addTo(key: string, id: string) {
  try {
    const arr: string[] = JSON.parse(localStorage.getItem(key) || '[]')
    if (!arr.includes(id)) arr.push(id)
    // 無制限に伸びないよう cap。
    localStorage.setItem(key, JSON.stringify(arr.slice(-500)))
  } catch {
    // ignore
  }
}

function removeFrom(key: string, id: string) {
  try {
    const arr: string[] = JSON.parse(localStorage.getItem(key) || '[]')
    localStorage.setItem(key, JSON.stringify(arr.filter((x) => x !== id)))
  } catch {
    // ignore
  }
}

export function useAutoPrint(storeId: string | undefined) {
  const [cfg, setCfg] = useState<PrinterCfg | null>(null)
  // #1960 溜まっていて確認待ちの伝票。null = 確認中でない。
  // **ここに入っている間は自動印刷を止める** (待っている間に溜まり続けない)。
  const [burst, setBurst] = useState<KitchenOrder[] | null>(null)
  const burstRef = useRef(false)
  const snoozeUntilRef = useRef(0)
  // #1167 レジ (会計レシート) プリンター。未設定なら厨房プリンターへフォールバック。
  const [receiptCfg, setReceiptCfg] = useState<PrinterCfg | null>(null)
  // #1207 レシート自動印刷の店舗設定。false = 希望者のみ (手動印刷 + デジタルレシート QR)。
  // 未設定 (undefined) は従来どおり自動印刷。厨房伝票・会計伝票はこの設定の対象外。
  const [receiptAutoPrint, setReceiptAutoPrint] = useState(true)

  // 設定を読み込む。storeId 変化に加え、プリンター設定の保存時に発火する
  // 'yokoso:printer-config-changed' でも再読込し、リロード無しで即反映する。
  useEffect(() => {
    // storeId 無しの店舗ページでは何もしない（cfg は既定 null のまま）。
    // effect 内で同期 setState を呼ばない（cascading render 警告の回避）。
    if (!storeId) return
    let cancelled = false
    // #2355 設定を保存した直後 (onCfgChange) は合流させない。mount の 1 回目は
    // 枠や画面の読み取りと 1 本にまとまる
    const load = (fresh = false) => {
      void api
        .getManagedStore(storeId, { fresh })
        .then((s: { printer?: Partial<PrinterCfg>; receiptPrinter?: Partial<PrinterCfg>; receiptAutoPrint?: boolean }) => {
          if (cancelled) return
          const p = s.printer || {}
          setCfg({ eposEnabled: !!p.eposEnabled, eposPrinterUrl: p.eposPrinterUrl || '' })
          const rp = s.receiptPrinter || {}
          setReceiptCfg({ eposEnabled: !!rp.eposEnabled, eposPrinterUrl: rp.eposPrinterUrl || '' })
          setReceiptAutoPrint(s.receiptAutoPrint !== false)
        })
        .catch(() => {
          if (!cancelled) {
            setCfg(null)
            setReceiptCfg(null)
          }
        })
    }
    // 設定保存時: 失敗記録をクリアして直近 (15分窓内) の未印刷レシートを再試行する。
    // 「URL 間違い → 保存し直したのに過去の会計が印刷されない」を防ぐ (#1167 FB)。
    // printed 側はクリアしないので二重印刷にはならない。mount 時はクリアしない
    // (ページ遷移のたびに死んだプリンタへ再送し続けるのを避ける)。
    const onCfgChange = () => {
      try {
        localStorage.removeItem(receiptFailuresKey(storeId))
      } catch {
        // ignore
      }
      load(true)
    }
    load()
    window.addEventListener(PRINTER_CONFIG_CHANGED, onCfgChange)
    return () => {
      cancelled = true
      window.removeEventListener(PRINTER_CONFIG_CHANGED, onCfgChange)
    }
  }, [storeId])

  //: #1960 確認ダイアログの「まとめて印刷」から呼ぶため、印刷の実体を外へ出す。
  //: effect 内のクロージャは外から触れないので ref 経由で渡す。
  const printOneRef = useRef<((orderId: string) => Promise<void>) | null>(null)

  useEffect(() => {
    if (!storeId || !cfg?.eposEnabled || !cfg.eposPrinterUrl) return
    const url = cfg.eposPrinterUrl
    let cancelled = false

    const printOne = async (orderId: string) => {
      if (inFlightPrints.has(orderId)) return
      // タブ間ロック内で printed を再確認 — 複数タブ同時検知の二重印刷防止 (#1178 FB)
      await withCrossTabLock(`kitchen:${orderId}`, async () => {
        if (readSet(printedKey(storeId)).has(orderId)) return
        inFlightPrints.add(orderId)
        try {
          const r = await api.getKitchenTicketPreview(orderId)
          if (!r.escposBase64) {
            addTo(failuresKey(storeId), orderId)
            return
          }
          const res = await sendEscposBase64(r.escposBase64, { printerUrl: url })
          if (res.ok) {
            removeFrom(failuresKey(storeId), orderId)
            addTo(printedKey(storeId), orderId)
            // #1222 サーバにも印刷済みを記録 (best-effort)。これが真実になるので
            // logout / 別端末 / キャッシュ削除でも二度印刷されない。
            void api.markKitchenPrinted(orderId).catch(() => {})
          } else {
            addTo(failuresKey(storeId), orderId)
          }
        } catch {
          addTo(failuresKey(storeId), orderId)
        } finally {
          inFlightPrints.delete(orderId)
        }
      })
    }

    printOneRef.current = printOne

    const tick = async () => {
      try {
        const data = await api.listStoreOrders(storeId)
        const raw: KitchenOrder[] = Array.isArray(data) ? data : data.orders || []
        const printed = readSet(printedKey(storeId))
        const failed = readSet(failuresKey(storeId))
        const now = Date.now()
        // 受付済み & 未印刷 & 未失敗(=失敗は手動リトライに委ねる) を古い順に印刷。
        // #1222 二重防御: (1) サーバの kitchenPrintedAt があれば印刷済み — localStorage
        // が消えても端末跨ぎで再印刷しない。(2) 作成から KITCHEN_WINDOW_MS を超えた
        // 古い注文は自動印刷しない(過去日の received が一斉再印刷されるのを防ぐ)。
        const toPrint = raw
          .filter(
            (o) =>
              o.status === 'received' &&
              !o.kitchenPrintedAt &&
              !printed.has(o.id) &&
              !failed.has(o.id) &&
              now - new Date(o.createdAt || 0).getTime() <= KITCHEN_WINDOW_MS,
          )
          .sort(
            (a, b) =>
              new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
          )
        // #1960 **溜まっていたら、いきなり刷らずに一度聞く。**
        // プリンターを繋いだ瞬間に何枚も出て、止める手段が無かった。
        // 確認中 (burstRef) は何もしない — 返事を待つ間に刷り始めたら意味が無い。
        if (burstRef.current) return
        if (toPrint.length >= KITCHEN_BURST_THRESHOLD && now >= snoozeUntilRef.current) {
          burstRef.current = true
          setBurst(toPrint)
          return
        }
        for (const o of toPrint) {
          if (cancelled) return
          await printOne(o.id)
        }
      } catch {
        // best-effort — 次の tick で再試行
      }
    }

    const timer = window.setInterval(() => void tick(), 5000)
    void tick() // 即時発火（設定 ON 直後に 5s 待たせない）
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [storeId, cfg?.eposEnabled, cfg?.eposPrinterUrl])

  // --- #1167 会計レシートの自動印刷 --------------------------------------
  // 印刷先: レジプリンター (receiptPrinter) が有効ならそこへ、未設定なら
  // 厨房プリンターへフォールバック。どちらも無効なら何もしない。
  // トリガーは /receipts/pending (直近 15 分の completed transaction)。
  // #2152 **支払い 1 回につき 1 枚**。卓会計も支払いの記録から刷る (払った注文の明細 +
  // お通し・席料 + 値引きが 1 枚に載る)。以前は卓会計を注文ごとに刷っていて、まとめて
  // 払った会計の紙が注文の数だけ出て、お通し・値引きがどこにも出なかった。
  const receiptUrl =
    receiptCfg?.eposEnabled && receiptCfg.eposPrinterUrl
      ? receiptCfg.eposPrinterUrl
      : cfg?.eposEnabled && cfg.eposPrinterUrl
        ? cfg.eposPrinterUrl
        : ''

  useEffect(() => {
    if (!storeId || !receiptUrl) return
    const url = receiptUrl
    let cancelled = false

    // fetchB64 は base64 / 'skip' (#1207 客の紙レシート設定で自動印刷しない) /
    // undefined (失敗) を返す。'skip' は printed 扱いにして再問い合わせを止める。
    const printOne = async (key: string, fetchB64: () => Promise<string | 'skip' | undefined>) => {
      if (inFlightPrints.has(key)) return
      // タブ間ロック内で printed を再確認 — 複数タブ同時検知の二重印刷防止 (#1178 FB)
      await withCrossTabLock(key, async () => {
        if (readSet(receiptPrintedKey(storeId)).has(key)) return
        inFlightPrints.add(key)
        try {
          const b64 = await fetchB64()
          if (b64 === 'skip') {
            addTo(receiptPrintedKey(storeId), key)
            return
          }
          if (!b64) {
            addTo(receiptFailuresKey(storeId), key)
            return
          }
          const res = await sendEscposBase64(b64, { printerUrl: url })
          if (res.ok) {
            removeFrom(receiptFailuresKey(storeId), key)
            addTo(receiptPrintedKey(storeId), key)
          } else {
            addTo(receiptFailuresKey(storeId), key)
          }
        } catch {
          addTo(receiptFailuresKey(storeId), key)
        } finally {
          inFlightPrints.delete(key)
        }
      })
    }

    const tick = async () => {
      const printed = readSet(receiptPrintedKey(storeId))
      const failed = readSet(receiptFailuresKey(storeId))

      // #1207 receiptAutoPrint=false (希望者のみ) の店は会計レシートの自動印刷を
      // スキップする。手動印刷 (取引履歴) と会計伝票 (下) はこの設定の対象外。
      if (receiptAutoPrint) {
        // 支払い (QR 単発決済・卓会計)
        try {
          const { transactions } = await api.storeAuthPendingReceipts(storeId, { settlements: true })
          for (const tx of transactions) {
            if (cancelled) return
            const key = `tx:${tx.id}`
            if (printed.has(key) || failed.has(key)) continue
            // #2152 切り替えの瞬間: 更新の前に注文ごとに刷った会計は、もう一度刷らない
            // (`order:` は以前の刷り方の印。中の注文が 1 つでも刷ってあれば客は紙を受け取っている)
            if ((tx.orderIds || []).some((id) => printed.has(`order:${id}`))) {
              addTo(receiptPrintedKey(storeId), key)
              continue
            }
            await printOne(key, async () => {
              const r = await api.storeAuthReceiptPreview(storeId, tx.id)
              return r.escposBase64
            })
          }
        } catch {
          // best-effort — 次の tick で再試行
        }
      }

      // #1178 会計伝票 (会計開始された卓のお会計スリップ)。dedup キーに
      // requestedAt を含める — 取消→再度会計開始の時は新しい伝票を刷り直す。
      try {
        const { checkouts } = await api.registerBillSlipsPending(storeId)
        for (const c of checkouts) {
          if (cancelled) return
          const key = `slip:${c.id}:${c.requestedAt || ''}`
          if (printed.has(key) || failed.has(key)) continue
          await printOne(key, async () => {
            const r = await api.registerBillSlipPreview(storeId, c.tableNumber)
            return r.escposBase64
          })
        }
      } catch {
        // best-effort — 次の tick で再試行
      }
    }

    const timer = window.setInterval(() => void tick(), 5000)
    // **レジが「払われた」と知った瞬間に起こす。** ポーリングだけだと
    // 会計直後に走るとは限らず、紙が出るまで客を待たせる。ポーリングは
    // 取りこぼし (客のスマホ決済・別端末の会計) の受け皿として残す。
    const offPaymentDone = onPaymentDone(() => void tick())
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
      offPaymentDone()
    }
  }, [storeId, receiptUrl, receiptAutoPrint])

  // --- #1960 溜まっていた伝票をどうするか ----------------------------------

  /**
   * まとめて印刷する。溜まっていたぶんを古い順に流す。
   *
   * **終わるまでダイアログを閉じない。** 先に閉じると、刷っている最中に画面を
   * 移られて途中で止まる (fetch が中断される)。ダイアログ側は待っている間
   * ボタンを無効にして回すので、進んでいることも分かる。
   */
  const printAllPending = useCallback(async () => {
    const list = burst || []
    for (const o of list) await printOneRef.current?.(o.id)
    burstRef.current = false
    setBurst(null)
  }, [burst])

  /**
   * 破棄する（刷らずに「印刷済み」にする）。
   *
   * **サーバの `kitchenPrintedAt` を立てる。** localStorage だけだと、
   * 別端末で開いた瞬間にまた一斉に出てくる。紙に出さないだけで「対応済み」に
   * するので、厨房の画面 (最近の伝票) には残る。
   */
  const discardPending = useCallback(async () => {
    const list = burst || []
    // **記録し切ってから閉じる。** 先に閉じると、サーバへの記録が終わる前に
    // 画面を移られて一部だけ残り、**次に開いたときに復活する**。
    // (押した直後に reload する E2E で実際に踏んだ)
    if (storeId) {
      for (const o of list) {
        addTo(printedKey(storeId), o.id)
        await api.markKitchenPrinted(o.id).catch(() => {})
      }
    }
    burstRef.current = false
    setBurst(null)
  }, [burst, storeId])

  /** 後で決める。閉じ込めないための逃げ道で、しばらくしたらまた聞く。 */
  const snoozePending = useCallback(() => {
    snoozeUntilRef.current = Date.now() + KITCHEN_BURST_SNOOZE_MS
    burstRef.current = false
    setBurst(null)
  }, [])

  return { pendingBurst: burst, printAllPending, discardPending, snoozePending }
}
